import React, { useEffect, useState } from 'react';
import { AddCircle16Regular, ArrowSort16Regular, ChevronLeft16Regular, Warning20Regular } from '@fluentui/react-icons';
import styles from './index.module.css';
import { formatCardDate, formatLastUpdated, getNoteLastUpdated, getNotePreview, getShortDisplayName } from './journalUtils';
import { getInitials } from '../../utils/commonUtils';

const EntryCardLastUpdated = ({ note }) => {
    const [, setTick] = useState(0);

    useEffect(() => {
        const intervalId = window.setInterval(() => setTick(tick => tick + 1), 30000);
        return () => window.clearInterval(intervalId);
    }, []);

    const lastUpdated = getNoteLastUpdated(note);

    return (
        <span className={styles.entryCardDate} title={formatCardDate(lastUpdated) || undefined}>
            {formatLastUpdated(lastUpdated)}
        </span>
    );
};

const EntriesPane = ({
    selectedSection,
    sortedNotes,
    editingNoteId,
    sortOrder,
    isSectionsCollapsed,
    projectUsersAll,
    onToggleSectionsCollapsed,
    onToggleSort,
    onAddEntry,
    onSelectEntry,
}) => {
    const getMemberFullName = id => projectUsersAll?.find(item => item.value.user_id === id)?.value?.full_name || '';

    const entryCount = sortedNotes?.length || 0;

    return (
        <aside className={styles.entriesPane}>
            <div className={styles.entriesHeader}>
                <div className={styles.entriesTitleRow}>
                    <div className={styles.entriesHeaderLeft}>
                        <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={onToggleSectionsCollapsed}
                            title={isSectionsCollapsed ? 'Expand sections' : 'Collapse sections'}
                            aria-label={isSectionsCollapsed ? 'Expand sections' : 'Collapse sections'}>
                            <ChevronLeft16Regular color="var(--Neutral-Gray-6)" style={{ transform: isSectionsCollapsed ? 'rotate(180deg)' : 'none' }} />
                        </button>
                        <div className={styles.entriesHeaderText}>
                            <span className={styles.entriesSectionName}>{selectedSection?.name || 'Journal'}</span>
                            <span className={styles.entriesCount}>
                                {entryCount} {entryCount === 1 ? 'entry' : 'entries'}
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.iconBtn}
                        onClick={onToggleSort}
                        title={sortOrder === 'desc' ? 'Sorted: Newest first' : 'Sorted: Oldest first'}
                        aria-label="Sort entries">
                        <ArrowSort16Regular color="var(--Neutral-Gray-6)" />
                    </button>
                </div>

                <button type="button" className={styles.newEntryBtn} onClick={onAddEntry} disabled={!selectedSection}>
                    <AddCircle16Regular />
                    New entry
                </button>
            </div>

            {selectedSection?.is_project_default && (
                <div className={styles.noticeBanner}>
                    <div className={styles.noticeBannerInner}>
                        <div className="me-2">
                            <Warning20Regular color="#f8047e" />
                        </div>
                        <span className={styles.noticePrefix}>
                            Important Notice: Project Updates are visible to all project users. Share only information intended for the entire project audience.
                        </span>
                    </div>
                </div>
            )}

            <div className={styles.entriesCards}>
                {sortedNotes.map(note => {
                    const { title, preview } = getNotePreview(note?.notes);
                    const fullName = getMemberFullName(note.created_by);
                    const shortName = getShortDisplayName(fullName) || 'Unknown';
                    const initials = getInitials(fullName) || '?';
                    const isSelected = editingNoteId === note.id;

                    return (
                        <div
                            key={note.id}
                            className={`${styles.entryCard} ${isSelected ? styles.entryCardSelected : ''}`}
                            onClick={() => onSelectEntry(note)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onSelectEntry(note);
                                }
                            }}>
                            <p className={styles.entryCardTitle}>{title}</p>
                            {preview ? <p className={styles.entryCardPreview}>{preview}</p> : null}
                            <div className={styles.entryCardFooter}>
                                <div className={styles.entryCardAuthor}>
                                    <span className={styles.entryCardAvatar}>{initials}</span>
                                    <span className={styles.entryCardAuthorName}>{shortName}</span>
                                </div>
                                <EntryCardLastUpdated note={note} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </aside>
    );
};

export default EntriesPane;
